import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { createSpaLinkClickHandler } from '../../utils/spaLinkUtils';
import './sim-info-panel.css';

/**
 * SimInfoPanel — боковая панель информации о SIM-карте
 * По макету Figma 26-04 wa Proxy Manager (nodes 541-81797, 541-81800, 541-81608)
 * Открывается справа от таблицы при клике на три точки
 * Анимация open/close как у SimFilterPanel
 */

// ==================== SVG ICONS ====================

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ==================== BADGE COMPONENTS ====================

const StatusBadge = ({ status }) => {
  const s = (status || '').toLowerCase();
  let cls = 'sip__badge';
  if (s.includes('недоступен') || s === 'unavailable' || s === 'not_working') cls += ' sip__badge--red';
  else if (s.includes('доступен') || s === 'available' || s === 'working') cls += ' sip__badge--green';
  else if (s.includes('неизвестно') || s === 'unknown') cls += ' sip__badge--yellow';
  return <span className={cls}>{status}</span>;
};

const isSimFrozenStatus = (status) => {
  const s = (status || '').toLowerCase();
  return s.includes('заморожен') || s === 'frozen';
};

const SimStatusBadge = ({ status }) => {
  const s = (status || '').toLowerCase();
  let cls = 'sip__badge';
  if (s.includes('работает') || s === 'working') cls += ' sip__badge--green';
  else if (s.includes('свободен') || s === 'free') cls += ' sip__badge--gray';
  else if (s.includes('резерв спб')) cls += ' sip__badge--cyan';
  else if (s.includes('резерв мск')) cls += ' sip__badge--purple';
  else if (s.includes('проверить') || s === 'check') cls += ' sip__badge--yellow';
  else if (s.includes('заменить') || s === 'replace') cls += ' sip__badge--red';
  else if (isSimFrozenStatus(status)) cls += ' sip__badge--frozen';
  return <span className={cls}>{status}</span>;
};

const OperatorBadge = ({ operator }) => {
  const s = (operator || '').toLowerCase();
  let cls = 'sip__badge';
  if (s.includes('мтс') || s === 'mts') cls += ' sip__badge--mts';
  else if (s.includes('мегафон') || s === 'megafon') cls += ' sip__badge--megafon';
  else if (s.includes('tele2') || s.includes('т2')) cls += ' sip__badge--tele2';
  else if (s.includes('билайн') || s === 'beeline') cls += ' sip__badge--beeline';
  return <span className={cls}>{operator}</span>;
};

const CopyableText = ({ value, copySuccessMessage, ariaLabelPrefix }) => {
  const text = String(value || '').trim();
  const canCopy = Boolean(text && text !== '—');

  const handleCopy = async () => {
    if (!canCopy) return;
    try {
      await navigator.clipboard.writeText(text);
      message.success(copySuccessMessage);
    } catch {
      message.error('Не удалось скопировать');
    }
  };

  if (!canCopy) {
    return <span className="sip__text">—</span>;
  }

  return (
    <button
      type="button"
      className="sip__text sip__text--copy"
      onClick={handleCopy}
      aria-label={`${ariaLabelPrefix} ${text}`}
      title="Нажмите, чтобы скопировать"
      tabIndex={0}
    >
      {text}
    </button>
  );
};

// ==================== MAIN COMPONENT ====================

const SimInfoPanel = ({
  isVisible = false,
  row = null,
  onClose,
  onDelete,
  onEdit,
  isMobile = false,
}) => {
  const navigate = useNavigate();
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(isVisible);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const prevVisibleRef = useRef(isVisible);
  const networkPortsModemPath = useMemo(() => {
    const params = new URLSearchParams();
    const krakenServerId = row?._raw?.kraken_server_id;
    const accountingNumber = row?._raw?.modem_number;
    const serverName = String(row?.server || '').trim();
    const modemDisplay = String(row?.modemNumber || '').trim();

    if (serverName) {
      params.set('server', serverName);
    }
    if (modemDisplay) {
      params.set('modem', modemDisplay);
    }

    const ksIdStr = krakenServerId !== null && krakenServerId !== undefined
      ? String(krakenServerId).trim()
      : '';
    const acctStr = accountingNumber !== null && accountingNumber !== undefined
      ? String(accountingNumber).trim()
      : '';

    // modem_server_id — PK kraken_servers.id, не учётный № (modem_number / 3-й октет).
    // Если совпадают — в URL только modem + server (иначе Network Ports ищет не тот модем).
    if (ksIdStr && (!acctStr || ksIdStr !== acctStr)) {
      params.set('modem_server_id', ksIdStr);
    }

    const query = params.toString();
    return query ? `/network-ports?${query}` : '';
  }, [row]);

  useEffect(() => {
    const wasVisible = prevVisibleRef.current;
    prevVisibleRef.current = isVisible;

    if (isVisible && !wasVisible) {
      setShouldRender(true);
      setIsClosing(false);
    } else if (!isVisible && wasVisible) {
      setIsClosing(true);
      const timer = setTimeout(() => {
        setShouldRender(false);
        setIsClosing(false);
      }, 450);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  useEffect(() => {
    if (!isMobile || !shouldRender) {
      return undefined;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isMobile, shouldRender]);

  if (!shouldRender || !row) return null;

  // Как в SimTable: red (<=сегодня), blue (1-2-й день), black (3-й день)
  const paymentUrgency = (() => {
    if (!row.nextPayment) return null;
    const parts = row.nextPayment.split('.');
    if (parts.length !== 3) return null;
    const date = new Date(parts[2], parts[1] - 1, parts[0]);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    const diffDays = Math.round((date - now) / (1000 * 60 * 60 * 24));
    if (diffDays <= 0) return 'red';
    if (diffDays === 1 || diffDays === 2) return 'blue';
    if (diffDays === 3) return 'black';
    return null;
  })();

  // Общий контент панели
  const panelContent = (
    <>
      {/* Заголовок */}
      <div className="sip__header">
        <span className="sip__title">Информация о Sim</span>
        <button
          type="button"
          className="sip__close"
          onClick={onClose}
          aria-label="Закрыть"
          tabIndex={0}
        >
          <CloseIcon />
        </button>
      </div>

      {/* Скроллируемый контент */}
      <div className={isMobile ? 'sip__scroll-content' : 'sip__body'}>
        {/* Группа 1: Модем, SIM, ГЕО */}
        <div className="sip__group">
          <div className="sip__line">
            <span className="sip__label">Модем</span>
            <StatusBadge status={row.modemStatus} />
          </div>
          <div className="sip__line">
            <span className="sip__label">SIM</span>
            <SimStatusBadge status={row.simStatus} />
          </div>
          {isSimFrozenStatus(row.simStatus) && row.freezeUntil && String(row.freezeUntil).trim() !== '' && (
            <div className="sip__line sip__line--freeze-until">
              <span className="sip__label">Заморожен до</span>
              <span className="sip__text">{row.freezeUntil}</span>
            </div>
          )}
          <div className="sip__line">
            <span className="sip__label">ГЕО</span>
            <span className="sip__text">{row.geo || '—'}</span>
          </div>
        </div>

        {/* Группа 2: Сервер, № Модема, Номер, Оператор */}
        <div className="sip__group">
          <div className="sip__line">
            <span className="sip__label">Сервер</span>
            <span className="sip__text">{row.server || '—'}</span>
          </div>
          <div className="sip__line">
            <span className="sip__label">№ Модема</span>
            {networkPortsModemPath ? (
              <a
                href={networkPortsModemPath}
                className="sip__text sip__text--link"
                onClick={createSpaLinkClickHandler(navigate, networkPortsModemPath)}
                aria-label={`Сетевые порты модема ${row.modemNumber}`}
                tabIndex={0}
              >
                {row.modemNumber}
              </a>
            ) : (
              <span className="sip__text">{row.modemNumber || '—'}</span>
            )}
          </div>
          <div className="sip__line">
            <span className="sip__label">Номер</span>
            <CopyableText
              value={row.phoneNumber}
              copySuccessMessage="Номер скопирован"
              ariaLabelPrefix="Скопировать номер"
            />
          </div>
          <div className="sip__line">
            <span className="sip__label">Оператор</span>
            {row.operator
              ? <OperatorBadge operator={row.operator} />
              : <span className="sip__text">—</span>
            }
          </div>
        </div>

        {/* Группа 3: Сумма, Платежи */}
        <div className="sip__group">
          <div className="sip__line">
            <span className="sip__label">Сумма, ₽</span>
            <span className="sip__text">{row.amount || '—'}</span>
          </div>
          <div className="sip__line">
            <span className="sip__label">Последний платеж</span>
            <span className="sip__text">{row.lastPayment || '—'}</span>
          </div>
          <div className="sip__line">
            <span className="sip__label">Следующий платеж</span>
            <span className={`sip__text${paymentUrgency ? ` sip__text--urgency-${paymentUrgency}` : ''}`}>
              {row.nextPayment || '—'}
            </span>
          </div>
          <div className="sip__line">
            <span className="sip__label">Оформлена</span>
            <span className="sip__text">{row.registeredOn || '—'}</span>
          </div>
        </div>

        {/* Группа 4: Личный кабинет */}
        <div className="sip__group">
          <div className="sip__line">
            <span className="sip__label">Личный кабинет</span>
            <span className="sip__text">{row.registeredBy || '—'}</span>
          </div>
          {row.accountLink && (
            <a
              href={row.accountLink}
              className="sip__btn-link"
              target="_blank"
              rel="noopener noreferrer"
              tabIndex={0}
              aria-label="Личный кабинет"
            >
              Личный кабинет
            </a>
          )}
        </div>

        {/* Группа 5: IMEI, MAC */}
        <div className="sip__group">
          <div className="sip__line">
            <span className="sip__label">IMEI</span>
            <CopyableText
              value={row.imei}
              copySuccessMessage="IMEI скопирован"
              ariaLabelPrefix="Скопировать IMEI"
            />
          </div>
          <div className="sip__line">
            <span className="sip__label">MAC</span>
            <CopyableText
              value={row.mac}
              copySuccessMessage="MAC скопирован"
              ariaLabelPrefix="Скопировать MAC"
            />
          </div>
        </div>

        {/* Группа 6: Комментарий */}
        <div className="sip__group sip__group--comment">
          <span className="sip__label">Комментарий</span>
          <span className="sip__comment-text">{row.comment || '—'}</span>
        </div>
      </div>

      {/* Футер: Удалить + Редактировать */}
      <div className="sip__footer">
        <button
          type="button"
          className="sip__action-btn sip__action-btn--delete"
          onClick={() => setShowDeleteConfirm(true)}
          aria-label="Удалить"
          tabIndex={0}
        >
          Удалить
        </button>
        <button
          type="button"
          className="sip__action-btn sip__action-btn--edit"
          onClick={() => { if (onEdit) onEdit(row); }}
          aria-label="Редактировать"
          tabIndex={0}
        >
          Редактировать
        </button>
      </div>
    </>
  );

  // Модальное окно удаления (общее для обоих режимов)
  const deleteModal = showDeleteConfirm && (
    <div
      className="sip__modal-overlay"
      onClick={() => setShowDeleteConfirm(false)}
      role="dialog"
      aria-modal="true"
      aria-label="Подтверждение удаления"
    >
      <div className="sip__modal" onClick={(e) => e.stopPropagation()}>
        <div className="sip__modal-body">
          <p className="sip__modal-title">Удалить SIM-карту?</p>
          <p className="sip__modal-text">
            {row.phoneNumber || 'Выбранная SIM'} будет удалена без возможности восстановления.
          </p>
        </div>
        <div className="sip__modal-footer">
          <button
            type="button"
            className="sip__modal-btn sip__modal-btn--cancel"
            onClick={() => setShowDeleteConfirm(false)}
            tabIndex={0}
          >
            Отмена
          </button>
          <button
            type="button"
            className="sip__modal-btn sip__modal-btn--confirm"
            onClick={() => {
              setShowDeleteConfirm(false);
              if (onDelete) onDelete(row);
            }}
            tabIndex={0}
          >
            Удалить
          </button>
        </div>
      </div>
    </div>
  );

  // Мобильный режим — overlay bottom-sheet
  if (isMobile) {
    const mobileContent = (
      <>
        <div
          className="sip-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="sip-title"
        >
          <div className="sip sip--mobile">
            {panelContent}
          </div>
        </div>
        {deleteModal}
      </>
    );

    if (typeof document === 'undefined') {
      return mobileContent;
    }

    return createPortal(mobileContent, document.body);
  }

  // Десктопный режим — боковая панель
  return (
    <div className={`sip-wrapper ${isClosing ? 'sip-wrapper--closing' : ''}`}>
      <div className={`sip ${isClosing ? 'sip--closing' : ''}`}>
        {panelContent}
      </div>
      {deleteModal}
    </div>
  );
};

export default SimInfoPanel;
